import { computed, defineComponent } from "vue";
import { mapActions, mapState } from "pinia";
import { Container } from "aurelia-dependency-injection";
import { useVuelidate } from "@vuelidate/core";
import moment from "moment";

import {
    ApplicationEnum,
    ContactRoles,
    Gender,
    HomeHealthEthnicityEnum,
    HomeHealthRacesEnum,
    HospiceHispanicEthnicitiesEnum,
    HttpStatusCodeEnum,
    InternalReferralSource,
    LineOfServices,
    MaritalStatuses,
    ParentPermissionEnum,
    PermissionActionEnum,
    SourceOfReferralEnum
} from "../../../enums/enums";
import { BranchesService } from "../../../services/branches-service";
import { ToastrService } from "../../../services/toastr.service";
import { ReferralService } from "../../../services/referral-service";
import { IGeneratePatientChart, PatientService } from "../../../services/patient.service";
import { UserService } from "../../../services/user-service";
import { UserManager } from "oidc-client";
import { HomeHealthPatient } from "../../../resources-vue/vue-interfaces/i-homeHealth-patient";
import { INonAdmitHHPatientQuery, INonAdmitPatientQuery } from "../../../interfaces/i-get-referral";
import { IPhoneNumber } from "../../../interfaces/i-phone-number";
import { IGetPatientTag } from "../../../resources-vue/vue-interfaces/i-providers-service";
import { IApplicationAndAccountQueryParam } from "../../../resources-vue/vue-interfaces/i-get-referral";
import {
    ethnicityIndexMapping,
    raceIndexMapping
} from "../../../common/vue-helpers/modifiers/race-ethnicity-index-mapping";
import { CentralIntakeService } from "../../../services/central-intake.service";

import { PermissionManager } from "../../../common/utilities/permission-manager";
import { HospiceReferral } from "../../../resources-vue/vue-models/hospice-referral";
import { HomeCareReferral } from "../../../resources-vue/vue-models/homecare-referral";
import { AuthorizedContact } from "../../../resources-vue/vue-models/authorized-contact";
import { scrollToFirstError } from "../../../common/utilities/first-error-scroll";
import { useCreateReferralHosPal } from "./store/useCreateReferralHosPal";
import { useLoadingIndicator } from "../../Common/LoadingIndicator/store/useLoadingIndicator";
import { formatName } from "../../../common/format-name";

import PageLayout from "../../../resources-vue/vue-custom-elements/PageLayout/PageLayout.vue";
import Prompt from "../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import HosPalReferralInformation from "./components/HosPalReferralInformation/HosPalReferralInformation.vue";
import PayorInformation from "./components/PayorInformation/PayorInformation.vue";
import AdmissionInformation from "./components/PatientPayorInformation/AdmissionInformation/AdmissionInformation.vue";
import PatientAuthorizedContacts from "./components/PatientAuthorizedContacts/PatientAuthorizedContacts.vue";
import PatientHealthHistory from "./components/PatientHealthHistory/PatientHealthHistory.vue";
import PatientPreadmissionNotes from "./components/PatientPreadmissionNotes/PatientPreadmissionNotes.vue";
import DemographicsInformation from "./components/DemographicsInformation/DemographicsInformation.vue";
import PatientTags from "./components/PatientTags/PatientTags.vue";
import ClinicianInformation from "./components/ClinicianInformation/ClinicianInformation.vue";
import ReferralReadiness from "./components/ReferralReadiness/ReferralReadiness.vue";
import ReferralDocuments from "./components/ReferralDocuments/ReferralDocuments.vue";
import PatientGoals from "./components/PatientGoals/PatientGoals.vue";

export default defineComponent({
    components: {
        PageLayout,
        HosPalReferralInformation,
        PayorInformation,
        AdmissionInformation,
        PatientAuthorizedContacts,
        PatientHealthHistory,
        PatientPreadmissionNotes,
        DemographicsInformation,
        PatientTags,
        ClinicianInformation,
        ReferralReadiness,
        ReferralDocuments,
        PatientGoals
    },

    data() {
        return {
            _centralIntake: null,
            _branchesService: null,
            _toastrService: null,
            _patientService: null,
            _userService: null,
            _currentUsersService: null,
            _userManager: null,
            dialogRef: null,
            _referralService: null,
            _permissionManager: null,
            isFormLoading: false,
            isLoadError: false,
            isSaveInProgress: false,
            hasErrorOnSave: false,
            isCreate: false,
            canShowReferralDocuments: false,
            accountId: "" as string,
            referralId: "" as string,
            inquiryId: "" as string,
            newPatientId: "" as string,
            locationId: "" as string,
            patientApplication: "" as string,
            patientBranchId: "" as string,
            patientFirstName: "" as string,
            patientLastName: "" as string,
            isNonAdmit: false,
            providerId: "",
            isHisSection: true,
            ContactRoles: Array.from(ContactRoles.enumWithDescriptions.values()),
            palliativeApplicationId: ApplicationEnum.AxxessPalliative,
            hospiceApplicationId: ApplicationEnum.AxxessHospiceFE,
            currentApplication: ApplicationEnum.AxxessHospiceFE,
            notesSection: null,
            referredPatient: null as HomeHealthPatient,
            referringAuthorizedContact: null,
            referringPatientDiagnosis: null,
            lineOfServiceEnumMap: LineOfServices,
            internalReferralSource: InternalReferralSource,
            error: false,
            errors: null,
            agencyId: null as string,
            applicationId: null as ApplicationEnum,
            patientId: null as string,
            currentUserId: null as string,
            isExternalReferral: false as boolean,
            externalReferralId: null as string,
            _user: null,
            isPatient: false,
            isRefer: false,
            showPatientChartToastr: false as boolean,
            newReferralData: null,
            isConvertBackToPending: false,
            loadReferAuthorizedContact: false,
            v$: useVuelidate(),
            raceIndexMapping,
            ethnicityIndexMapping
        };
    },
    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },
    async created() {
        this._branchesService = Container.instance.get(BranchesService);
        this._toastrService = Container.instance.get(ToastrService);
        this._referralService = Container.instance.get(ReferralService);
        this._centralIntake = Container.instance.get(CentralIntakeService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._patientService = Container.instance.get(PatientService);
        this._userService = Container.instance.get(UserService);
        this._userManager = Container.instance.get(UserManager);
        if (this.$route.query.isConvertBackToPending === "true") {
            this.isConvertBackToPending = true;
        }
        if (this.$route.query?.isCreate === "true" && !this.$route.query.patientId) {
            this.isCreate = true;
        }
        if (this.$route.query?.isPatient == "true") {
            this.isPatient = true;
        }

        if (this.$route.query?.externalReferralId && this.$route.query?.isCreate === "true") {
            this.externalReferralId = this.$route.query?.externalReferralId as string;
            this.isExternalReferral = true;
            this.referralId = this.$route?.query.id as string;
            this.agencyId = this.$route.query?.accountId as string;
            this.applicationId = this.$route.query?.application as any;
        }
        if (this.$route.query?.isCreate === "true" && this.$route.query.patientId) {
            this.patientId = this.$route.query?.patientId as string;
            this.agencyId = this.$route.query?.accountId as string;
            this.isCreate = true;
        }
        if (this.$route.query?.application) {
            const application = Number(this.$route.query.application);
            if (Object.values(ApplicationEnum).includes(application)) {
                this.applicationId = application;
            }
        }
        if (this.$route.query?.newPatientId) {
            this.newPatientId = this.$route.query?.newPatientId as string;
        }
        if (this.$route.query?.application) {
            this.currentApplication = Number(this.$route.query.application);
            if (this.currentApplication == ApplicationEnum.AxxessPalliative) {
                this.isHisSection = false;
            }
        }
        if (this.$route.query?.patientApplication) {
            this.patientApplication = this.$route.query?.patientApplication as string;
        }
        if (this.$route.query?.patientBranchId) {
            this.patientBranchId = this.$route.query?.patientBranchId as string;
        }
        if (this.$route.query?.locationId) {
            this.locationId = this.$route.query?.locationId as string;
        }
        if (this.$route.query?.accountId) {
            this.accountId = this.$route.query.accountId as string;
        }
        if (this.$route.query?.isRefer === "true") {
            this.isRefer = true as boolean;
        }
        if (this.$route.query?.isNonAdmit === "true") {
            this.isNonAdmit = true as boolean;
        }
        if (this.$route.query?.inquiryId) {
            this.inquiryId = this.$route.query?.inquiryId as string;
        }
        if (this.$route.query?.includePatientCharts === "true") {
            this.showPatientChartToastr = true as boolean;
        }
        if (this.$route.query?.id) {
            this.referralId = this.$route.query.id as string;
            await this.loadReferral();
        } else {
            this._toastrService.error({
                title: `Error`,
                message: `There was a problem while creating this referral. Please try again. If the problem persists, please report it.`
            });
            this.$router.push("/axxess-central/intake/referral-list");
        }
        this.canShowReferralDocuments = this._permissionManager.checkPermissionByProduct(
            ParentPermissionEnum.documents,
            PermissionActionEnum.canView,
            this.currentApplication
        );
    },

    async mounted() {
        this.notesSection = this.$refs.notesSection;
        let hash = window.location.hash;

        if (!!hash) {
            this.notesSection.scrollIntoView();
        }
    },
    computed: {
        ...mapState(useCreateReferralHosPal, ["referral"])
    },

    methods: {
        ...mapActions(useCreateReferralHosPal, { setReferral: "SET_REFERRAL" }),
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),

        async loadReferral() {
            try {
                this.showLoading(true);
                this.isLoadError = false;
                this.isFormLoading = true;
                if (this.referralId && !this.isRefer && !this.isExternalReferral) {
                    let inquiryInfo;
                    let inquiryPhoneNumber: IPhoneNumber[] = [
                        {
                            number: "",
                            extension: "",
                            type: 0,
                            country: "840",
                            isPrimary: true
                        },
                        {
                            number: "",
                            extension: "",
                            type: 0,
                            country: "840",
                            isPrimary: false
                        }
                    ];
                    if (this.inquiryId) {
                        const payload: IApplicationAndAccountQueryParam = {
                            agencyId: this.accountId,
                            application: this.currentApplication
                        };

                        inquiryInfo = await this._centralIntake.getInquiryById(this.inquiryId, payload);
                        inquiryPhoneNumber[0].number = inquiryInfo.patientPhoneNumber;
                    }
                    let referral = (await this.getReferralData()) as HospiceReferral;
                    let newReferral = new HospiceReferral(this.currentApplication, referral);
                    newReferral.phoneNumbers = inquiryInfo
                        ? inquiryPhoneNumber
                        : newReferral.phoneNumbers.sort((item1: IPhoneNumber, item2: IPhoneNumber) => {
                              if (item1.isPrimary && !item2.isPrimary) {
                                  return -1;
                              } else if (!item1.isPrimary && item2.isPrimary) {
                                  return 1;
                              } else {
                                  return 0;
                              }
                          });
                    if (newReferral.phoneNumbers.findIndex((e) => !e.isPrimary) < 0) {
                        newReferral.phoneNumbers.push({
                            number: "",
                            extension: "",
                            type: 0,
                            country: null,
                            isPrimary: false
                        });
                    }
                    this.setReferral(newReferral);
                    await this.setProvider();
                } else if (this.referralId && this.isRefer && !this.isExternalReferral) {
                    let userName = "";
                    this.getCurrentUser();
                    this._user = await this._userManager.getUser();
                    if (this._user) {
                        userName = formatName(this._user.profile.given_name, this._user?.profile.family_name);
                    }
                    if (this.patientApplication === ApplicationEnum.AgencyCore.toString()) {
                        this.referredPatient = this.isPatient
                            ? await this.getHHReferPatientData()
                            : await this.getHHReferReferralData();
                        let newReferral = new HospiceReferral(this.currentApplication);
                        newReferral.id = String(this.referralId);
                        newReferral.patientId = String(this.newPatientId);
                        newReferral.accountId = String(this.accountId);
                        newReferral.application = Number(this.applicationId);
                        newReferral.referralDate = String(moment().format("MM/DD/YYYY"));
                        newReferral.locationId = String(this.locationId);
                        newReferral.lineOfService =
                            this.currentApplication == ApplicationEnum.AxxessPalliative
                                ? this.lineOfServiceEnumMap.enum.PalliativeCare
                                : this.lineOfServiceEnumMap.enum.Hospice;
                        newReferral.sourceOfReferral = SourceOfReferralEnum.Internal;
                        newReferral.internalReferralSource = this.internalReferralSource.enum.HomeHealth;
                        newReferral.agencyId = String(this.agencyId);
                        newReferral.internalReferralSourceId = this.currentUserId;
                        newReferral.internalReferralSourceName = userName;
                        newReferral.referralReason = "Internal referal from (Home Health) patient";

                        newReferral.firstName = this.patientFirstName = this.referredPatient.Demographics.FirstName;
                        newReferral.middleInitial = this.referredPatient.Demographics.MiddleInitial;
                        newReferral.lastName = this.patientLastName = this.referredPatient.Demographics.LastName;
                        newReferral.dateOfBirth = this.referredPatient.Demographics.DOB;
                        newReferral.gender = null;
                        if (
                            this.referredPatient.Demographics.Gender !== "0" &&
                            this.referredPatient.Demographics.Gender !== ""
                        ) {
                            newReferral.gender = Gender.getValueUsingKey(this.referredPatient.Demographics.Gender);
                        }
                        newReferral.socialSecurityNumber = this.referredPatient.Demographics.SSN;
                        let phoneNumbersHH = [
                            {
                                number: this.referredPatient.Demographics.PhonePreferred,
                                extension: "",
                                type: 0,
                                country: null as any,
                                isPrimary: true
                            },
                            {
                                number: this.referredPatient.Demographics.PhoneAlternate,
                                extension: "",
                                type: 0,
                                country: null as any,
                                isPrimary: false
                            }
                        ];
                        newReferral.phoneNumbers = phoneNumbersHH;
                        newReferral.email = this.referredPatient.Demographics.EmailAddress;
                        if (
                            this.referredPatient.Demographics.MaritalStatus &&
                            this.referredPatient.Demographics.MaritalStatus !== "0"
                        ) {
                            newReferral.maritalStatus = MaritalStatuses.getValueUsingKey(
                                this.referredPatient.Demographics.MaritalStatus
                            );
                        }

                        if (this.referredPatient.Demographics.RacesCollection.length !== 0) {
                            if (
                                this.referredPatient.Demographics.RacesCollection[0] ==
                                HomeHealthRacesEnum.PatientDeclinesToRespond
                            ) {
                                newReferral.declineToSpecifyRace = true;
                            } else {
                                newReferral.race = this.raceIndexMapping(
                                    ApplicationEnum.AgencyCore as number,
                                    this.currentApplication == ApplicationEnum.AxxessPalliative
                                        ? (ApplicationEnum.AxxessPalliative as number)
                                        : (ApplicationEnum.AxxessHospiceFE as number),
                                    this.referredPatient.Demographics.RacesCollection
                                );
                            }
                        }
                        if (this.referredPatient.Demographics.EthnicitiesOnlyCollection.length !== 0) {
                            if (
                                this.referredPatient.Demographics.EthnicitiesOnlyCollection[0] ==
                                HomeHealthEthnicityEnum.PatientDeclinesToRespond
                            ) {
                                newReferral.declineToSpecifyEthnicity = true;
                            } else {
                                if (this.currentApplication == ApplicationEnum.AxxessPalliative) {
                                    newReferral.ethnicityOnly = this.ethnicityIndexMapping(
                                        ApplicationEnum.AgencyCore as number,
                                        ApplicationEnum.AxxessPalliative as number,
                                        this.referredPatient.Demographics.EthnicitiesOnlyCollection
                                    )?.[0];
                                } else {
                                    newReferral.hispanicEthnicity =
                                        this.ethnicityIndexMapping(
                                            ApplicationEnum.AgencyCore as number,
                                            ApplicationEnum.AxxessHospiceFE as number,
                                            this.referredPatient.Demographics.EthnicitiesOnlyCollection
                                        )?.[0] || 0;
                                }
                            }
                        }
                        if (this.referredPatient.Demographics.PrimaryAddress) {
                            newReferral.primaryAddress.addressLine1 =
                                this.referredPatient.Demographics.PrimaryAddress.AddressLine1;
                            newReferral.primaryAddress.addressLine2 =
                                this.referredPatient.Demographics.PrimaryAddress.AddressLine2;
                            newReferral.primaryAddress.city = this.referredPatient.Demographics.PrimaryAddress.City;
                            newReferral.primaryAddress.county = this.referredPatient.Demographics.PrimaryAddress.County;
                            newReferral.primaryAddress.state = this.referredPatient.Demographics.PrimaryAddress.State;
                            newReferral.primaryAddress.zipCode = this.referredPatient.Demographics.PrimaryAddress.Zip;
                        }
                        newReferral.isVisitAddressSameAsPrimary = false;
                        if (this.referredPatient.Demographics.IsVisitAddress) {
                            newReferral.visitAddress.addressLine1 =
                                this.referredPatient.Demographics.VisitAddress.AddressLine1;
                            newReferral.visitAddress.addressLine2 =
                                this.referredPatient.Demographics.VisitAddress.AddressLine2;
                            newReferral.visitAddress.city = this.referredPatient.Demographics.VisitAddress.City;
                            newReferral.visitAddress.county = this.referredPatient.Demographics.VisitAddress.County;
                            newReferral.visitAddress.state = this.referredPatient.Demographics.VisitAddress.State;
                            newReferral.visitAddress.zipCode = this.referredPatient.Demographics.VisitAddress.Zip;
                        }
                        if (this.referredPatient.Demographics.MailingAddress) {
                            newReferral.mailingAddress.addressLine1 =
                                this.referredPatient.Demographics.MailingAddress.AddressLine1;
                            newReferral.mailingAddress.addressLine2 =
                                this.referredPatient.Demographics.MailingAddress.AddressLine2;
                            newReferral.mailingAddress.city = this.referredPatient.Demographics.MailingAddress.City;
                            newReferral.mailingAddress.county = this.referredPatient.Demographics.MailingAddress.County;
                            newReferral.mailingAddress.state = this.referredPatient.Demographics.MailingAddress.State;
                            newReferral.mailingAddress.zipCode = this.referredPatient.Demographics.MailingAddress.Zip;
                            newReferral.isMailingAddressSameAsPrimary =
                                this.referredPatient.Demographics.SameAsPhysicalAddress;
                        }
                        newReferral.medicareBeneficiaryIdentificationNA = this.referredPatient.Insurance.IsMbiNA;
                        newReferral.medicareBeneficiaryIdentification = this.referredPatient.Insurance.MbiNumber;
                        newReferral.medicaidNumber = this.referredPatient.Insurance.MedicaidNumber;
                        newReferral.notAMedicaid = this.referredPatient.Insurance.IsMedicaidNA;
                        newReferral.admissionType = 0;
                        newReferral.benefitPeriodNumber = null;
                        newReferral.admissionSource = 0;
                        this.referringAuthorizedContact = this.referredPatient.Contacts;

                        this.referringPatientDiagnosis = this.referredPatient.Clinical.PrimaryPatientDiagnosisCodes;
                        if (this.referredPatient.Clinical.PatientDiagnosisCodes.length) {
                            newReferral.diagnoses.push({
                                description: this.referredPatient.Clinical.PatientDiagnosisCodes[0]?.ShortDescription,
                                code: this.referredPatient.Clinical.PatientDiagnosisCodes[0]?.DiagnosisCode,
                                order: 1,
                                billable: true
                            });
                        }
                        this.setReferral(newReferral);
                        await this.setProvider();
                    }
                    if (
                        this.patientApplication === ApplicationEnum.AxxessHospiceFE.toString() ||
                        this.patientApplication === ApplicationEnum.AxxessPalliative.toString()
                    ) {
                        let referral = this.isPatient
                            ? (await this.getHospicePatientData()).data.model
                            : await this.getHosPalReferralData();

                        let newReferral = new HospiceReferral(this.currentApplication, referral);
                        if (referral.phoneNumbers) {
                            let filteredPhoneNumbers = referral.phoneNumbers.map((item: IPhoneNumber) => {
                                delete item.id;
                                return item;
                            });
                            newReferral.phoneNumbers = filteredPhoneNumbers;
                        }
                        this.patientFirstName = newReferral.firstName;
                        this.patientLastName = newReferral.lastName;
                        newReferral.id = String(this.referralId);
                        newReferral.patientId = String(this.newPatientId);
                        newReferral.accountId = String(this.accountId);
                        newReferral.application = Number(this.applicationId);
                        newReferral.referralDate = String(moment().format("MM/DD/YYYY"));
                        newReferral.locationId = String(this.locationId);
                        newReferral.lineOfService =
                            this.currentApplication == ApplicationEnum.AxxessPalliative
                                ? this.lineOfServiceEnumMap.enum.PalliativeCare
                                : this.lineOfServiceEnumMap.enum.Hospice;
                        newReferral.sourceOfReferral = SourceOfReferralEnum.Internal;
                        newReferral.internalReferralSource =
                            this.currentApplication == ApplicationEnum.AxxessPalliative
                                ? this.internalReferralSource.enum.Hospice
                                : this.lineOfServiceEnumMap.enum.PalliativeCare;
                        newReferral.agencyId = String(this.agencyId);
                        newReferral.internalReferralSourceId = this.currentUserId;
                        newReferral.internalReferralSourceName = userName;
                        if (newReferral.communityLiaisonName) {
                            newReferral.communityLiaisonName = "";
                            newReferral.communityLiaisonId = "";
                        }
                        newReferral.attendingPhysicianName = "";
                        newReferral.attendingPhysicianId = "";
                        newReferral.careTeamId = null;
                        newReferral.tags = [] as IGetPatientTag[];
                        const authContacts = await this.getAuthorizedContacts(this.patientId);
                        authContacts?.forEach((contact: AuthorizedContact) => {
                            this.loadReferAuthorizedContact = true;
                            contact.id = null;
                            contact.application = this.currentApplication;
                            if (this.patientApplication === ApplicationEnum.AxxessHospiceFE.toString()) {
                                contact.cahpsSurvey = null;
                                contact.isCahpsSurveyRecipient = false;
                            }
                            this.addAuthorizedContact(contact);
                        });

                        if (this.currentApplication == ApplicationEnum.AxxessPalliative) {
                            newReferral.referralReason = "Internal referal from (Hospice) patient";
                            if (newReferral.race?.length) {
                                newReferral.race = this.raceIndexMapping(
                                    ApplicationEnum.AxxessHospiceFE as number,
                                    ApplicationEnum.AxxessPalliative as number,
                                    newReferral.race
                                );
                            }
                            if (newReferral.hispanicEthnicity) {
                                newReferral.ethnicity = this.ethnicityIndexMapping(
                                    ApplicationEnum.AxxessHospiceFE as number,
                                    ApplicationEnum.AxxessPalliative as number,
                                    [newReferral.hispanicEthnicity]
                                );
                                newReferral.ethnicityOnly = newReferral.ethnicity[0];
                            }
                        } else {
                            newReferral.referralReason = "Internal referal from (Palliative) patient";
                            if (!newReferral.declineToSpecifyRace && newReferral.race?.length) {
                                newReferral.race = this.raceIndexMapping(
                                    ApplicationEnum.AxxessPalliative as number,
                                    ApplicationEnum.AxxessHospiceFE as number,
                                    newReferral.race
                                );
                            }
                            if (!newReferral.declineToSpecifyEthnicity && newReferral.ethnicityOnly) {
                                newReferral.hispanicEthnicity = this.ethnicityIndexMapping(
                                    ApplicationEnum.AxxessPalliative as number,
                                    ApplicationEnum.AxxessHospiceFE as number,
                                    [newReferral.ethnicityOnly]
                                )?.[0];
                            }
                            newReferral.admissionType = 0;
                            newReferral.benefitPeriodNumber = null;
                            newReferral.admissionSource = 0;
                        }
                        this.setReferral(newReferral);
                        await this.setProvider();
                    }
                    if (this.patientApplication === ApplicationEnum.HomeCare.toString()) {
                        let referral = this.isPatient
                            ? await this.getHomeCarePatientData()
                            : await this.getHomeCareReferralData();
                        let newReferral = new HospiceReferral(this.currentApplication);
                        let filteredHCPhoneNumbers: any[] = [];
                        referral.phoneNumbers.forEach((item: any) => {
                            let check: boolean = true;
                            if (this.isPatient) {
                                check = filteredHCPhoneNumbers
                                    ? filteredHCPhoneNumbers.some((hcNumber) => hcNumber.number == item.number)
                                    : false;
                            } else {
                                check = false;
                            }
                            if (!check && (item.ordinal == 1 || item.ordinal == 2)) {
                                let hcNumber: IPhoneNumber = {} as IPhoneNumber;
                                hcNumber.number = item.number;
                                hcNumber.extension = item.extension;
                                hcNumber.type = item.type !== 4 ? item.type : 0;
                                hcNumber.country = item.country;
                                hcNumber.isPrimary = item.ordinal == 1 ? true : false;
                                filteredHCPhoneNumbers.push(hcNumber);
                            }
                        });
                        if (filteredHCPhoneNumbers.findIndex((e) => !e.isPrimary) < 0) {
                            filteredHCPhoneNumbers.push({
                                number: "",
                                extension: "",
                                type: 0,
                                country: null,
                                isPrimary: false
                            });
                        }
                        await this.setProvider();

                        newReferral.id = String(this.referralId);
                        newReferral.patientId = String(this.newPatientId);
                        newReferral.accountId = String(this.accountId);
                        newReferral.application = Number(this.applicationId);
                        newReferral.referralDate = String(moment().format("MM/DD/YYYY"));
                        newReferral.locationId = String(this.locationId);
                        newReferral.lineOfService =
                            this.currentApplication == ApplicationEnum.AxxessPalliative
                                ? this.lineOfServiceEnumMap.enum.PalliativeCare
                                : this.lineOfServiceEnumMap.enum.Hospice;
                        newReferral.sourceOfReferral = SourceOfReferralEnum.Internal;
                        newReferral.internalReferralSource = this.internalReferralSource.enum.HomeCare;
                        newReferral.agencyId = String(this.agencyId);
                        newReferral.internalReferralSourceId = this.currentUserId;
                        newReferral.internalReferralSourceName = userName;
                        newReferral.referralReason = "Internal referal from (Home Care) patient";
                        newReferral.providerId = this.providerId;

                        newReferral.firstName = this.patientFirstName = referral.firstName;
                        newReferral.middleInitial = this.isPatient ? referral.middleInitial : referral.middleName;
                        newReferral.lastName = this.patientLastName = referral.lastName;
                        newReferral.dateOfBirth = String(moment(referral.dob).format("MM/DD/YYYY"));
                        newReferral.phoneNumbers = filteredHCPhoneNumbers;
                        newReferral.gender = this.isPatient
                            ? Gender.getValueUsingKey(referral.gender)
                            : Number(referral.gender);
                        newReferral.email = referral.emailAddress;
                        if (this.isPatient) {
                            if (referral.races.length !== 0) {
                                newReferral.race = this.raceIndexMapping(
                                    ApplicationEnum.HomeCare as number,
                                    this.currentApplication == ApplicationEnum.AxxessPalliative
                                        ? (ApplicationEnum.AxxessPalliative as number)
                                        : (ApplicationEnum.AxxessHospiceFE as number),
                                    referral.races
                                );
                            }
                            if (referral.ethnicities.length !== 0) {
                                if (this.currentApplication == ApplicationEnum.AxxessPalliative) {
                                    newReferral.ethnicityOnly = this.ethnicityIndexMapping(
                                        ApplicationEnum.HomeCare as number,
                                        ApplicationEnum.AxxessPalliative as number,
                                        referral.ethnicities
                                    )?.[0];
                                } else {
                                    newReferral.hispanicEthnicity =
                                        this.ethnicityIndexMapping(
                                            ApplicationEnum.HomeCare as number,
                                            ApplicationEnum.AxxessHospiceFE as number,
                                            referral.ethnicities
                                        )?.[0] || 0;
                                }
                            }
                        }
                        newReferral.socialSecurityNumber = this.referral.socialSecurityNumber;
                        if (newReferral.maritalStatus && referral.maritalStatus !== "0") {
                            newReferral.maritalStatus = this.isPatient
                                ? MaritalStatuses.getValueUsingKey(referral.maritalStatus)
                                : Number(referral.maritalStatus);
                        }
                        newReferral.primaryAddress.addressLine1 = this.isPatient
                            ? referral.address.line1
                            : referral.primaryAddress.addressLine1;
                        newReferral.primaryAddress.addressLine2 = this.isPatient
                            ? referral.address.line2
                            : referral.primaryAddress.addressLine2;
                        newReferral.primaryAddress.city = this.isPatient
                            ? referral.address.locality
                            : referral.primaryAddress.city;
                        newReferral.primaryAddress.county = this.isPatient
                            ? referral.address.subAdministrativeArea
                            : referral.primaryAddress.county;
                        newReferral.primaryAddress.state = this.isPatient
                            ? referral.address.subAdministrativeArea
                            : referral.primaryAddress.state;
                        newReferral.primaryAddress.zipCode = this.isPatient
                            ? referral.address.zipcode
                            : referral.primaryAddress.zipCode;

                        newReferral.isVisitAddressSameAsPrimary = referral.isVisitAddressSameAsPrimary;
                        newReferral.isMailingAddressSameAsPrimary = referral.isMailingAddressSameAsPrimary;

                        if (!referral.isVisitAddressSameAsPrimary && referral.visitAddress) {
                            newReferral.visitAddress.addressLine1 = this.isPatient
                                ? referral.address.line1
                                : referral.visitAddress.addressLine1;
                            newReferral.visitAddress.addressLine2 = this.isPatient
                                ? referral.address.line2
                                : referral.visitAddress.addressLine2;
                            newReferral.visitAddress.city = this.isPatient
                                ? referral.address.locality
                                : referral.visitAddress.city;
                            newReferral.visitAddress.county = this.isPatient
                                ? referral.address.subAdministrativeArea
                                : referral.visitAddress.county;
                            newReferral.visitAddress.state = this.isPatient
                                ? referral.address.subAdministrativeArea
                                : referral.visitAddress.state;
                            newReferral.visitAddress.zipCode = this.isPatient
                                ? referral.address.zipcode
                                : referral.visitAddress.zipCode;
                        }

                        if (!referral.isMailingAddressSameAsPrimary && referral.mailingAddress) {
                            newReferral.mailingAddress.addressLine1 = this.isPatient
                                ? referral.address.line1
                                : referral.mailingAddress.addressLine1;
                            newReferral.mailingAddress.addressLine2 = this.isPatient
                                ? referral.address.line2
                                : referral.mailingAddress.addressLine2;
                            newReferral.mailingAddress.city = this.isPatient
                                ? referral.address.locality
                                : referral.mailingAddress.city;
                            newReferral.mailingAddress.county = this.isPatient
                                ? referral.address.subAdministrativeArea
                                : referral.mailingAddress.county;
                            newReferral.mailingAddress.state = this.isPatient
                                ? referral.address.subAdministrativeArea
                                : referral.mailingAddress.state;
                            newReferral.mailingAddress.zipCode = this.isPatient
                                ? referral.address.zipcode
                                : referral.mailingAddress.zipCode;
                        }

                        newReferral.medicaidNumber = referral.medicaidNumber;
                        newReferral.medicaidPending = this.isPatient
                            ? referral.medicaidPending
                            : referral.medicaidPending;
                        newReferral.medicareBeneficiaryIdentificationNA = referral.medicareBeneficiaryIdentificationNA;
                        newReferral.medicareBeneficiaryIdentification = referral.medicareBeneficiaryIdentification;
                        referral.admissionSource = 0;
                        referral.admissionType = 0;
                        this.setReferral(newReferral);
                    }
                } else if (this.isCreate && this.isExternalReferral) {
                    let newReferral = new HospiceReferral(this.currentApplication);
                    const payload = {
                        Id: this.externalReferralId
                    };
                    let externalReferral = await this._referralService.getExternalReferralById(payload);
                    externalReferral = externalReferral.data;
                    let phoneNumbers = [
                        {
                            number: externalReferral.PrimaryPhoneNumber?.split("-").join(""),
                            extension: "",
                            type: 1,
                            country: null as any,
                            isPrimary: true,
                            ordinal: 1,
                            ordinalNumber: 1
                        }
                    ];

                    await this.setProvider();

                    newReferral.accountId = this.agencyId;
                    newReferral.referralDate = String(moment().format("MM/DD/YYYY"));
                    newReferral.application = Number(this.applicationId);
                    newReferral.firstName = externalReferral.FirstName;
                    newReferral.sourceOfReferral = SourceOfReferralEnum.Facility;
                    newReferral.middleInitial = externalReferral.MiddleInitial;
                    newReferral.lastName = externalReferral.LastName;
                    newReferral.dateOfBirth = externalReferral.DateOfBirth;
                    newReferral.email = externalReferral.Email;
                    newReferral.gender = externalReferral.Gender;
                    newReferral.primaryAddress.addressLine1 = externalReferral.AddressLine1;
                    newReferral.primaryAddress.addressLine2 = externalReferral.AddressLine2;
                    newReferral.primaryAddress.city = externalReferral.City;
                    newReferral.primaryAddress.state = externalReferral.State;
                    newReferral.primaryAddress.zipCode = externalReferral.ZipCode;
                    newReferral.primaryAddress.county = externalReferral.County;
                    newReferral.medicareBeneficiaryIdentificationNA = false;
                    newReferral.medicareBeneficiaryIdentification = "";
                    newReferral.phoneNumbers[0] = phoneNumbers[0];
                    newReferral.lineOfService = this.currentApplication == ApplicationEnum.AxxessHospiceFE ? 1 : 2;
                    newReferral.patientId = this.externalReferralId;
                    newReferral.id = this.referralId;
                    newReferral.admissionType = 0;
                    newReferral.benefitPeriodNumber = null;
                    newReferral.admissionSource = 0;
                    this.setReferral(newReferral);
                } else {
                    throw new Error("Referral Id is missing");
                }
            } catch (e) {
                this.isLoadError = true;
                console.error(e);
            } finally {
                this.showLoading(false);
                this.isFormLoading = false;
            }
        },

        async getCurrentUser() {
            const agencyId = this.accountId;
            try {
                let response = await this._userService.getCurrentUserId({ agencyId: agencyId });
                this.currentUserId = response.userId;
            } catch (err) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while getting this User Id. Please try again. If the problem persists, please report it.`
                });
            }
        },

        async getReferralData() {
            const queryParam = {
                application: this.currentApplication,
                accountId: this.accountId,
                id: this.referralId
            };
            let referral = (await this._referralService.getReferral(this.referralId, queryParam)) as HospiceReferral;
            return referral;
        },

        async getHospicePatientData() {
            const queryParam = {
                application: this.patientApplication,
                agencyId: this.patientBranchId,
                patientId: this.patientId
            };

            let referral = await this._patientService.getHospicePatientById(queryParam);
            return referral;
        },

        async getHosPalReferralData() {
            try {
                const queryParam = {
                    application: this.patientApplication,
                    accountId: this.patientBranchId,
                    id: this.patientId
                };
                const referral = await this._referralService.getReferral(this.patientId, queryParam);
                return referral;
            } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while getting ${
                        this.patientApplication === ApplicationEnum.AxxessPalliative.toString()
                            ? "Palliative"
                            : "Hospice"
                    } referral. Please try again. If the problem persists, please report it.`
                });
            }
        },

        async getAuthorizedContacts(id: string) {
            try {
                const payload: IApplicationAndAccountQueryParam = {
                    application: Number(this.patientApplication),
                    agencyId: this.patientBranchId
                };
                const authorizedContacts = await this._referralService.getContacts(id, payload);
                return authorizedContacts;
            } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while getting Authorized Contact(s). Please report it.`
                });
            }
        },

        async addAuthorizedContact(contact: AuthorizedContact) {
            try {
                contact.accountId = this.accountId;
                contact.agencyId = this.accountId;
                contact.application = this.applicationId;
                const response = await this._referralService.addContact(this.referralId, contact);
                if (!response.data.isSuccessful) {
                    this._toastrService.error({
                        title: `Error`,
                        message: "There was a problem while adding a contact. Please try again."
                    });
                }
            } catch (e) {
                console.error(e);
                this._toastrService.error({
                    title: `Error`,
                    message: "There was a problem while adding a contact. Please try again."
                });
            } finally {
                this.loadReferAuthorizedContact = false;
            }
        },

        async getHomeCarePatientData() {
            const queryParam = {
                application: this.patientApplication,
                agencyId: this.patientBranchId,
                patientId: this.patientId
            };

            let referral = (await this._patientService.getHCPatientById(queryParam)).data;
            return referral;
        },

        async getHomeCareReferralData() {
            const queryParam = {
                application: this.patientApplication,
                accountId: this.patientBranchId,
                id: this.patientId
            };

            let referral = (await this._referralService.getHomeCareReferral(
                this.referralId,
                queryParam
            )) as HomeCareReferral;
            return referral;
        },

        async getHHReferPatientData() {
            try {
                const result = await this._patientService.getHHPatientAllData({
                    agencyId: this.patientBranchId,
                    patientId: this.patientId
                });
                if (result) {
                    let tempReferral = result.data;
                    return tempReferral;
                }
            } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while getting this Home Health patient. Please try again. If the problem persists, please report it.`
                });
            }
        },

        async getHHReferReferralData() {
            try {
                const result = await this._patientService.getEditHHReferral({
                    referralId: this.patientId,
                    agencyId: this.patientBranchId,
                    application: this.$route.query?.application
                });
                if (result) {
                    let tempReferral = result.data;
                    tempReferral.AgencyId = this.accountId;
                    return tempReferral;
                }
            } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while getting this Home Health patient. Please try again. If the problem persists, please report it.`
                });
            }
        },

        async setProvider() {
            if (!this.isRefer && !!this.referral.providerId) {
                this.providerId = this.referral.providerId;
            } else {
                let patientBranchId = this.locationId;
                let branches = await this._branchesService.getAllBranches();
                let patientBranch = branches.find((branch: any) => branch.id === patientBranchId);
                this.providerId = patientBranch?.providerId ?? "";
            }
        },
        checkLatAndLong() {
            return (
                !this.referral.primaryAddress.isValidated &&
                !this.referral.primaryAddress.isBypassed &&
                !this.referral.primaryAddress.longitude &&
                !this.referral.primaryAddress.latitude
            );
        },
        async create() {
            let addressNotValidated = this.checkLatAndLong();
            if (addressNotValidated) {
                this._toastrService.error({
                    title: `Error`,
                    message: `Please enter valid address`
                });
                return;
            }
            if (!this.referral.hasPreviousAddress && !!this.referral?.previousAddress) {
                this.referral.previousAddress = null;
            }

            let isModelValid = await this.v$.$validate();
            let isError = false as boolean;

            if (
                this.referral.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.NoNotOfHispanicLatinoOrSpanishOrigin
                ) &&
                (this.referral.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.YesMexicanAmericanChicano
                ) ||
                    this.referral.hispanicEthnicityValues?.includes(HospiceHispanicEthnicitiesEnum.YesPuertoRican) ||
                    this.referral.hispanicEthnicityValues?.includes(HospiceHispanicEthnicitiesEnum.YesCuban) ||
                    this.referral.hispanicEthnicityValues?.includes(
                        HospiceHispanicEthnicitiesEnum.YesAnotherHispanicLatinoOrSpanishOrigin
                    ) ||
                    this.referral.hispanicEthnicityValues?.includes(
                        HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond
                    ))
            ) {
                isError = true;
            } else if (
                this.referral.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.PatientUnableToRespond
                ) &&
                this.referral.hispanicEthnicityValues?.includes(HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond)
            ) {
                isError = true;
            } else if (
                this.referral.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond
                ) &&
                this.referral.hispanicEthnicityValues?.length > 1
            ) {
                isError = true;
            }

            if (isModelValid && !isError) {
                try {
                    this.hasErrorOnSave = false;
                    this.isSaveInProgress = true;
                    this.newReferralData = await this.createReferral();
                    if (this.newReferralData.referralId) {
                        this._toastrService.success({
                            title: `Referral Created`,
                            message: `The Referral <b>${this.referral.lastName} ${this.referral.firstName}</b> has been successfully created.`
                        });

                        // Once referral is create successfully delete that form external referral list
                        if (this.isExternalReferral) {
                            await this._referralService.deleteExternalReferralById({
                                Id: this.externalReferralId
                            });
                        }
                    }
                    if (this.showPatientChartToastr) {
                        this._toastrService.info({
                            message: `The requested patient/client chart document is in-progress, you will be notified when the document is completed.`
                        });
                    }
                    if (this.isRefer && this.isNonAdmit) {
                        this.doNonAdmit();
                    }
                    if (!this.isConvertBackToPending) {
                        this.$router.push("/axxess-central/intake/referral-list");
                    }
                } catch (err) {
                    this._toastrService.error({
                        title: `Error`,
                        message: `There was a problem while creating this referral. Please try again. If the problem persists, please report it.`
                    });
                } finally {
                    this.isSaveInProgress = false;
                }
            } else {
                if (!isModelValid) {
                    scrollToFirstError();
                    this._toastrService.error({
                        title: `Error`,
                        message: `Please fix the validation errors in the form.`
                    });
                }
                if (isError) {
                    this._toastrService.error({
                        title: "Error",
                        message:
                            "Invalid Combination for HOPE Ethnicity: Y must be selected alone; A cannot be combined with B-E or Y; B-E cannot be combined with Y; X cannot be combined with Y."
                    });
                }
            }
        },

        async createReferral() {
            try {
                this.referral.id = this.referralId;
                this.referral.agencyId = this.accountId;
                let response = null;
                if (this.currentApplication == ApplicationEnum.AxxessHospiceFE) {
                    response = await this._referralService.createHospiceReferral(this.referral);
                }

                if (this.currentApplication == ApplicationEnum.AxxessPalliative) {
                    response = await this._referralService.createPalliativeReferral(this.referral);
                }
                if (this.showPatientChartToastr) {
                    await this.generatePatientCharts(response.referralId);
                }
                return response;
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        async update() {
            if (!this.referral.hasPreviousAddress && !!this.referral?.previousAddress) {
                this.referral.previousAddress = null;
            }
            let isModelValid = await this.v$.$validate();
            let isError = false as boolean;
            if (
                this.referral.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.NoNotOfHispanicLatinoOrSpanishOrigin
                ) &&
                (this.referral.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.YesMexicanAmericanChicano
                ) ||
                    this.referral.hispanicEthnicityValues?.includes(HospiceHispanicEthnicitiesEnum.YesPuertoRican) ||
                    this.referral.hispanicEthnicityValues?.includes(HospiceHispanicEthnicitiesEnum.YesCuban) ||
                    this.referral.hispanicEthnicityValues?.includes(
                        HospiceHispanicEthnicitiesEnum.YesAnotherHispanicLatinoOrSpanishOrigin
                    ) ||
                    this.referral.hispanicEthnicityValues?.includes(
                        HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond
                    ))
            ) {
                isError = true;
            } else if (
                this.referral.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.PatientUnableToRespond
                ) &&
                this.referral.hispanicEthnicityValues?.includes(HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond)
            ) {
                isError = true;
            } else if (
                this.referral.hispanicEthnicityValues?.includes(
                    HospiceHispanicEthnicitiesEnum.PatientDeclinesToRespond
                ) &&
                this.referral.hispanicEthnicityValues?.length > 1
            ) {
                isError = true;
            }
            if (isModelValid && !isError) {
                try {
                    this.isSaveInProgress = true;
                    await this.updateReferral();
                    this._toastrService.success({
                        title: `Referral Updated`,
                        message: `The Referral <b>${this.referral.lastName} ${this.referral.firstName}</b> has been successfully updated.`
                    });
                } catch (err) {
                    console.error(err);
                    if (err.status === HttpStatusCodeEnum.NotModified) {
                        this._toastrService.info({
                            title: `The referral <b>${this.referral.lastName} ${this.referral.firstName}</b> is up to date`,
                            message: `There were no changes made in ${this.referral.lastName} ${this.referral.firstName}'s profile.`
                        });
                    } else {
                        this._toastrService.error({
                            title: `Error`,
                            message: `There was a problem while saving this referral. Please try again. If the problem persists, please report it.`
                        });
                    }
                } finally {
                    this.isSaveInProgress = false;
                }
            } else {
                if (!isModelValid) {
                    this._toastrService.error({
                        title: `Error`,
                        message: `Please fix the validation errors in the form.`
                    });
                }
                if (isError) {
                    this._toastrService.error({
                        title: "Error",
                        message:
                            "Invalid Combination for HOPE Ethnicity: Y must be selected alone; A cannot be combined with B-E or Y; B-E cannot be combined with Y; X cannot be combined with Y."
                    });
                }
            }
        },
        async updateReferral() {
            if (this.currentApplication == ApplicationEnum.AxxessHospiceFE) {
                await this._referralService.editHospiceReferral(this.referral);
            } else {
                await this._referralService.editPalliativeReferral(this.referral);
            }
        },
        async goToReferralList(isCancelAction = false) {
            let promptOptions = {
                message: "Are you sure you want to leave the page? Please make sure you saved your work.",
                okText: "Yes, I'm Sure",
                cancelText: "Never Mind"
            };
            this.dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: (options) => {
                    if (options.data === "success") {
                        if (this.isExternalReferral) {
                            this.$router.push("/axxess-central/intake/external-referral-list");
                        } else {
                            if (isCancelAction) {
                                this.$router.back();
                            } else {
                                this.$router.push("/axxess-central/intake/referral-list");
                            }
                        }
                    }
                }
            });
        },

        async handleConvertToPending(referral: any) {
            this.isConvertBackToPending = true;
            await this.create();
            try {
                const payload = {
                    accountId: String(referral.accountId),
                    referralId: String(this.newReferralData.referralId),
                    application: Number(referral.application)
                };
                await this._patientService.loadPatientReferral(referral.patientId, payload);
                if (referral.application == ApplicationEnum.AxxessHospiceFE) {
                    this.$router.push(
                        `/axxess-central/patients/patient-edit/?patientId=${referral.patientId}&accountId=${referral.accountId}&application=${referral.application}&patientReferralId=${this.newReferralData.referralId}`
                    );
                } else {
                    this.$router.push(
                        `/axxess-central/patients/patient-edit-pal/${referral.patientId}/accountId/${referral.accountId}/patientReferralId/${this.newReferralData.referralId}`
                    );
                }
            } catch (err) {
                console.error(err);
                this._toastrService.error({
                    title: `Error`,
                    message: "There was a problem while converting the referral. Please try again."
                });
            }
        },

        doNonAdmit() {
            let nonAdmitDate = String(this.$route.query.nonAdmitDate);
            nonAdmitDate = moment(nonAdmitDate, "MM-DD-YYYY").format("MM/DD/YYYY");
            const nonAdmitReasonId = String(this.$route.query.nonAdmitReasonId);
            const nonAdmitReason = String(this.$route.query.nonAdmitReason);
            const patientAgencyId = this.patientBranchId;
            const patientApplication = this.patientApplication;
            const patientId = this.patientId;
            let promptOptions = {
                message: "Are you sure you want to update this patient to Non-Admit?",
                okText: "Yes, I'm Sure",
                cancelText: "No"
            };
            this.dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        try {
                            if (this.isPatient) {
                                if (patientApplication === ApplicationEnum.AgencyCore.toString()) {
                                    let queryObj: INonAdmitHHPatientQuery = {
                                        AgencyId: patientAgencyId.toString(),
                                        NonAdmitDate: nonAdmitDate,
                                        Reason: [nonAdmitReason],
                                        Application: ApplicationEnum.AgencyCore
                                    };
                                    await this._patientService.updatePatientToNonAdmit(patientId, queryObj);
                                } else if (patientApplication === ApplicationEnum.HomeCare.toString()) {
                                    let queryObj = {
                                        AgencyId: patientAgencyId.toString(),
                                        NonAdmitDate: nonAdmitDate,
                                        Reason: nonAdmitReason,
                                        Application: ApplicationEnum.HomeCare,
                                        ReasonId: nonAdmitReasonId
                                    };
                                    await this._patientService.updatePatientToNonAdmitHC(patientId, queryObj);
                                } else {
                                    let newNonAdmitData: INonAdmitPatientQuery = {
                                        nonAdmitDate: nonAdmitDate,
                                        nonAdmitReasonId: nonAdmitReasonId,
                                        nonAdmitComments: ""
                                    };
                                    let queryObj = JSON.parse(JSON.stringify(newNonAdmitData));
                                    queryObj.agencyId = patientAgencyId;
                                    queryObj.application = patientApplication;
                                    await this._patientService.updatePatientToNonAdmit(patientId, queryObj);
                                }
                            } else {
                                let queryObj = {
                                    accountId: patientAgencyId.toString(),
                                    nonAdmitDate: nonAdmitDate,
                                    nonAdmitReasonId: nonAdmitReasonId,
                                    nonAdmitReason: [nonAdmitReason],
                                    application: Number(this.patientApplication)
                                };
                                await this._centralIntake.updateReferralToNonAdmit(patientId, queryObj);
                            }
                            this._toastrService.success({
                                title: `${this.isPatient ? "Patient" : "Referral"} Converted to Non-Admit`,
                                message: `The ${this.isPatient ? "patient" : "referral"} <b>${this.patientLastName}, ${
                                    this.patientFirstName
                                }</b> has been converted to Non-Admit.`
                            });
                        } catch (error) {
                            this._toastrService.error({
                                title: `Error -${this.isPatient ? "Patient" : "Referral"} convert to Non-Admit`,
                                message: `There was a problem while converting the ${
                                    this.isPatient ? "patient" : "referral"
                                } <b>${this.patientLastName}, ${this.patientFirstName}</b>. Please try again.`
                            });
                        }
                    }
                }
            });
        },

        async generatePatientCharts(generateReferralId: string) {
            const payload: IGeneratePatientChart = {
                patientId: this.patientId,
                application: Number(this.patientApplication),
                agencyId: this.patientBranchId,
                referralId: generateReferralId
            };

            try {
                return await this._patientService.generatePatientCharts(payload);
            } catch (error) {
                console.error(error);
            }
        }
    }
});
